/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import './bootstrap'
import './filters'
import router from './router'
import auth from './auth'
import store from './store'
import broadcast from "./broadcast"
import {mapActions} from 'vuex'
import {html_mixin} from "./mixins"
import * as ComplianceBundleTypes from './constants/compliance-bundle-types'

// Set global mixins here
Vue.mixin(html_mixin)

// auth components
Vue.component('login', require('./components/login.vue').default)

// admin dashboard components
Vue.component('app-header', require('./components/app-header.vue').default)
Vue.component('app-footer', require('./components/app-footer.vue').default)
Vue.component('app-sidebar', require('./components/app-sidebar.vue').default)
Vue.component('advanced-filters', require('./components/advanced-filters.vue').default)
Vue.component('subscription-status-tag', require('./components/subscription-status-tag.vue').default)
Vue.component('services-health', require('./components/services-health.vue').default)
Vue.component('app-monitor', require('./components/app-monitor.vue').default)
Vue.component('app-monitor-graph', require('./components/app-monitor-graph.vue').default)
Vue.component('admin-selector', require('./components/admin-selector.vue').default)
Vue.component('user-company', require('./components/user-company.vue').default)
Vue.component('user-rate', require('./components/user-rate.vue').default)
/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */
const app = new Vue({
    data: {
        user: auth.user,
        ComplianceBundleTypes,
    },

    beforeCreate() {
        axios.get('/build-info.json').then(res => {
            this.setEnv(res.data.env)
        }).catch(err => {
            console.log(err)
        })
    },

    created() {
        // Handle admin session restore after stop-impersonation
        const urlParams = new URLSearchParams(window.location.search)
        const restore = urlParams.get('restore')
        const token = urlParams.get('token')
        const companyId = urlParams.get('company_id')
        if (restore && token) {
            localStorage.setItem('admin_api_token', token)
            window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
            window.location.href = '/admin/company/' + companyId + '?tab=company-user-list'
            return
        }
    },

    mounted() {
        VueEvent.listen('COMPLIANCE_BUNDLE_OPERATION_DONE', (data) => {
            // only listen to events from current and company
            let message = ''
            const bundle_type = ComplianceBundleTypes.COMPLIANCE_BUNDLE_TYPES[data.bundle_type]
            switch (data.operation) {
                case 'Enable':
                    if (data.status == 'failed') {
                        message = `Failed to enable <strong>${bundle_type} #${data.id}</strong> for <strong>${data.company_name}</strong>.`
                    } else {
                        message = `<strong>${bundle_type} #${data.id}</strong> for <strong>${data.company_name}</strong> has been enabled.`
                    }
                    this.pullIdFromLoadingUpdateBundle(data.id)
                    break
                case 'Delete':
                    if (data.status == 'failed') {
                        message = `Failed to delete <strong>${bundle_type} #${data.id}</strong> for <strong>${data.company_name}.`
                    } else {
                        message = `<strong>${bundle_type} #${data.id}</strong> for <strong>${data.company_name}</strong> has been deleted.`
                    }
                    this.pullIdFromLoadingDeleteBundle(data.id)
                    break
                case 'Sync':
                    if (data.status == 'failed') {
                        message = `Failed to sync <strong>${bundle_type}</strong> for <strong>${data.company_name}</strong>.`
                    } else {
                        message = `Sync operation of <strong>${bundle_type}</strong> for <strong>${data.company_name}</strong> has been completed.`
                    }
                    this.pullComplianceSyncType(data.bundle_type)
                    break
                default:
                    return
            }
            this.$notify({
                title: `${data.operation} bundle`,
                type: (data.status === 'failed') ? 'error' : 'success',
                showClose: true,
                dangerouslyUseHTMLString: true,
                message,
            })
        })
    },

    methods: {
        ...mapActions(['setEnv']),

        getPlans() {
            axios.get(`/api/admin/plan`, {
                params: {
                    all: true
                }
            }).then(res => {
                this.setPlans(res.data)
            }).catch(err => {
                this.handleErrors(err.response)
            })
        },

        getRates() {
            axios.get(`/api/admin/rate`, {
                params: {
                    all: 1
                }
            }).then(res => {
                this.setRates(res.data)
            }).catch(err => {
                this.handleErrors(err.response)
            })
        },

        getCompanies() {
            axios.get('/api/admin/company', {
                mode: 'no-cors',
            }).then(res => {
                this.setCompanies(res.data)
            }).catch(err => {
                this.handleErrors(err.response)
            })
        },

        getUsers() {
            return axios.get('/api/admin/user', {
                mode: 'no-cors',
            }).then(res => {
                this.setUsers(res.data)
            }).catch(err => {
                this.handleErrors(err.response)
            })
        },

        getAdmins() {
            return axios.get('/api/admin/admins', {
                mode: 'no-cors',
            }).then(res => {
                this.setAdmins(res.data)
            }).catch(err => {
                this.handleErrors(err.response)
            })
        },

        handleErrors(response, title = null, onCloseNotificationCallback = null) {
            // Default duration = 4500 per 'Notification' component docs
            const notificationDuration = (onCloseNotificationCallback) ? 10000 : 4500
            if (response && response.status) {
                let message = ''
                switch (response.status) {
                    case 401:
                    case 403:
                        auth.check().then((res) => {
                        }).catch((err) => {
                            window.location.href = '/'
                        })
                        message = 'You are not authorized to make this request.'
                        break
                    case 404:
                        message = 'Requested resource not found.'
                        break
                    case 422:
                        message = "<ul class='mt-3'>"
                        for (let index in response.data.errors) {
                            message += "<li>" + response.data.errors[index] + "</li>"
                        }
                        message += "</ul>"
                        break
                    case 500:
                        message = 'We\'re having problems right now, please try later.'
                        break
                    case 400:
                        message = response.data.error
                        break
                }
                this.$notify({
                    offset: 95,
                    title: (title) ? title : 'Error',
                    dangerouslyUseHTMLString: true,
                    message: message,
                    type: 'error',
                    showClose: true,
                    onClose: onCloseNotificationCallback || null,
                    duration: notificationDuration,
                })
            }
        },

        ...mapActions(['setPlans', 'setRates', 'setAdmins', 'pullIdFromLoadingUpdateBundle', 'pullIdFromLoadingDeleteBundle', 'pullComplianceSyncType'])
    },
    el: '#app',
    router,
    store,
    watch: {
        'user.authenticated': async function (newVal, oldVal) {
            if (newVal && !oldVal) {
                let getPlans = this.getPlans()
                let getRates = this.getRates()
                let getAdmins = this.getAdmins()
                broadcast.listener(this.user)
                await Promise.all([getPlans, getRates, getAdmins])
            }
        }
    },

    beforeDestroy() {
        // destroy listeners
        VueEvent.unlisten('COMPLIANCE_BUNDLE_OPERATION_DONE')
    }
})
